/**
 * "Invoices this will create" (§8.1, Board 11 and Board 12): `Invoiced · For
 * · Amount · Due` on a `96 / 1fr / 96 / 80` grid, a Total row, and the
 * `Adds up to the visits ✓` chip where the rows come to the plan.
 *
 * Every word is `invoiceTable()`'s and every number the planner's; this file
 * only lays them out. Nothing is folded — see `invoiceTable`.
 */
import {
  BILLING_TAB, INVOICES_ADD_UP, invoiceTable, type BillingWordsContext, type InvoiceTablePlan,
  type InvoiceTone,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { SectionLabel } from '../../components/primitives.js'

const GRID = 'grid grid-cols-[96px_minmax(0,1fr)_96px_80px] gap-x-2'

const TONE: Record<InvoiceTone, string> = {
  date: 'text-text-muted',
  event: 'text-text-muted',
  approval: 'text-info',
  closed: 'text-warn',
}

export function InvoiceTable({
  plan, ctx, empty,
}: { plan: InvoiceTablePlan; ctx: BillingWordsContext; empty: string }) {
  const table = invoiceTable(plan, ctx)
  return (
    <div data-invoice-table="">
      <SectionLabel className="block">{BILLING_TAB.invoicesHeading}</SectionLabel>
      {table.rows.length === 0
        ? <p className="m-0 mt-2 text-[12.5px] text-text-faint">{empty}</p>
        : (
          <div className="mt-2">
            <div className={cn(GRID, 'pb-[5px]')}>
              {BILLING_TAB.columns.map((column, index) => (
                <span key={column} className={cn('text-[10.5px] font-semibold uppercase tracking-[0.06em]',
                  'text-text-faint', index >= 2 && 'text-right')}>
                  {column}
                </span>
              ))}
            </div>
            {table.rows.map((row, index) => (
              <div key={index} data-invoice-row=""
                className={cn(GRID, 'items-center border-t border-line-faint py-1.5 text-[12.5px]')}>
                <span className={cn('truncate font-semibold', TONE[row.tone])}>{row.invoiced}</span>
                <span className="truncate text-text-muted">{row.for}</span>
                <b className="tnum text-right font-bold text-text">{row.amount}</b>
                <span className="text-right text-[11.5px] text-text-faint">{row.due}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 border-t border-line pt-2 text-[12.5px]">
              <b className="text-text">{table.totalLabel}</b>
              <span className="tnum ml-auto text-right font-bold text-text">{table.total}</span>
              {table.addsUp && (
                <span className="inline-flex h-5 shrink-0 items-center whitespace-nowrap rounded-[6px]
                                 bg-accent-soft px-[7px] text-[11px] font-semibold text-accent-dark">
                  {INVOICES_ADD_UP}
                </span>
              )}
            </div>
          </div>
        )}
    </div>
  )
}
